<script setup>
// Vue Imports
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

// Component Logic
const props = defineProps({
    ariaLabel: {
        type: String,
        required: true
    },
    buttonType: {
        type: String,
        required: true,
        validator: (value) =>
            [
                'primary',
                'secondary',
                'unset',
                'tertiary',
                'primaryRebrand',
                'secondaryRebrand',
                'tertiaryRebrand',
                'wawButton',
                'primaryRebrandMWP',
                'mwpWhite',
            ].includes(value)
    },
    element: {
        type: String,
        default: 'button'
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        default: 'button'
    },
    small: {
        type: Boolean,
        default: false,
        required: false,
    },
})

const componentRef = ref(null)

const tagAllowList = ['a', 'button', 'RouterLink']
const tag = computed(() => {
    const { element } = props

    const listPosition = tagAllowList?.findIndex(
        (item) => item?.toLowerCase() === element?.toLowerCase()
    )

    return tagAllowList?.[listPosition] ?? 'button'
})

const styleLookup = {
    unset: '', // No style to the button (Allows external override)
    primary: {
        default: 'px-5 py-3 flex justify-center items-center gap-x-1.5 rounded-md bg-mwi-light-blue2 text-base font-bold leading-6 text-white dark:text-dbd-dark-blue shadow-sm transform transition duration-300  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:bg-gray-400 hover:scale-101 hover:bg-indigo-400 pt-3',
    },
    secondary: {
        default: 'px-5 py-3 block max-w-md gap-x-1.5 rounded-md bg-mwi-blue2 text-center text-lg font-semibold text-white dark:text-dbd-dark-blue shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 pt-3',
    },
    tertiary: {
        default: 'px-5 py-3 flex items-center font-medium tracking-wide text-white capitalize transition-colors duration-300 transform bg-slate-500 rounded-lg hover:bg-mwi-light-blue2 focus:outline-none focus:ring focus:ring-mwi-light-blue2 focus:ring-opacity-80 pt-3',
    },
    primaryRebrand: {
        default: 'button-padding primary-mwi group flex items-center justify-center cursor-pointer rounded-full max-w-max',
    },
    secondaryRebrand: {
        default: 'button-padding secondary-mwi group flex items-center justify-center cursor-pointer rounded-full',
    },
    tertiaryRebrand: {
      default: 'button-padding tertiary-mwi group flex items-center justify-center cursor-pointer',
    },
    wawButton: {
      default: 'px-5 py-3 bg-mwi-light-blue2 dark:text-dbd-dark-blue disabled:bg-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 hover:scale-101 hover:bg-indigo-400 flex justify-center rounded-xl px-8 pt-3 text-lg font-bold leading-6 text-white shadow-sm transform transition duration-300',
    },
    primaryRebrandMWP: {
        default: 'button-padding primary-mwp group flex items-center justify-center cursor-pointer rounded-full max-w-max',
    },
    mwpWhite: {
        default: 'button-padding white-button hover:border hover:border-gray-100 hover:text-white group flex items-center justify-center cursor-pointer',
    },
}
const buttonStyle = computed(() => {
    const { buttonType } = props

    return styleLookup?.[buttonType]
})

defineExpose({ componentRef })
</script>

<template>
    <component
        :is="tag"
        ref="componentRef"
        role="button"
        :type="type"
        :aria-label="ariaLabel"
        :class="[buttonStyle?.default, props.small ? '!text-sm !pt-1 !pb-2 !px-3' : '']"
    >
        <div class="flex flex-row gap-3 justify-center items-center">
            <svg v-if="isLoading" class="animate-spin h-5 w-5 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <slot />
        </div>
    </component>
</template>
